import type { Question, QuestionTemplate, Response } from '../content/types';
import { createRng } from './rng';

/** Build one concrete question from a template and a seed. */
export function generate(template: QuestionTemplate, seed: string): Question {
  const built = template.gen(createRng(seed));
  return {
    ...built,
    templateId: template.id,
    topicId: template.topicId,
  } as Question;
}

/**
 * Grade a response.
 *
 * Untouched questions are wrong, not errors — leaving one blank and hitting
 * Check is a legitimate thing to do, and it should reveal the answer rather
 * than block on a validation message.
 */
export function isCorrect(question: Question, response: Response): boolean {
  if (response === null) return false;

  switch (question.kind) {
    case 'mc':
      return response === question.answerIndex;
    case 'ruler':
      return response === question.answerSixteenth;
    case 'bolt':
      return response === question.answerIndex;
    case 'tri':
      return response === question.answerSide;
    case 'slider':
      return typeof response === 'number' && Math.abs(response - question.answer) <= question.tolerance;
    case 'numeric':
      return typeof response === 'number' && Math.abs(response - question.answer) <= question.tolerance;
  }
}

/**
 * The specific mistake behind a wrong multiple-choice pick, when the template
 * named one. This is what turns "wrong" into a lesson.
 */
export function trapFor(question: Question, response: Response): string | null {
  if (question.kind !== 'mc' || typeof response !== 'number') return null;
  if (response === question.answerIndex) return null;
  return question.options[response]?.trap ?? null;
}

/** Display text for the correct answer, used in feedback. */
export function answerLabel(question: Question): string {
  switch (question.kind) {
    case 'mc':
      return question.options[question.answerIndex]?.label ?? '';
    case 'ruler': {
      const n = question.answerSixteenth;
      return n === 0 ? '0' : n === 16 ? '1' : reduceSixteenth(n);
    }
    case 'bolt':
      return `hole ${question.answerIndex + 1}`;
    case 'tri':
      return { opp: 'the opposite side', adj: 'the adjacent side', hyp: 'the hypotenuse' }[
        question.answerSide
      ];
    case 'slider':
    case 'numeric':
      return `${question.answer.toFixed(question.kind === 'numeric' ? question.places : 3)}${question.unit}`;
  }
}

function reduceSixteenth(n: number): string {
  let num = n;
  let den = 16;
  while (num % 2 === 0 && den % 2 === 0) {
    num /= 2;
    den /= 2;
  }
  return `${num}/${den}`;
}
